import * as React from 'react';

import FocusLock from 'react-focus-lock';

const LIVE_REGION_SELECTOR = [
  '#cpt-notification-container',
  '[aria-live]',
  '[role="alert"]',
  '[role="status"]',
  '[role="log"]',
].join(',');

interface Props {
  enabled?: boolean;
  returnFocusTo?(): HTMLElement | null | undefined;
  children: React.ReactNode;
}

function hideOthers(root: HTMLElement): () => void {
  const hidden: [Element, string | null][] = [];
  const hide = (element: Element) => {
    if (element.matches(LIVE_REGION_SELECTOR)) return;
    if (element.querySelector(LIVE_REGION_SELECTOR)) {
      Array.from(element.children).forEach(hide);
      return;
    }
    hidden.push([element, element.getAttribute('aria-hidden')]);
    element.setAttribute('aria-hidden', 'true');
  };
  let node: HTMLElement = root;
  while (node !== document.body && node.parentElement) {
    const parent: HTMLElement = node.parentElement;
    Array.from(parent.children).forEach((child) => {
      if (child !== node) hide(child);
    });
    node = parent;
  }
  return () =>
    hidden.forEach(([element, previous]) => {
      if (previous === null) element.removeAttribute('aria-hidden');
      else element.setAttribute('aria-hidden', previous);
    });
}

function findModal(element: HTMLElement | null): HTMLElement | null {
  let node = element;
  while (node && !node.classList.contains('modal')) node = node.parentElement;
  return node;
}

const ModalFocusLock: React.FC<Props> = ({
  enabled = false,
  returnFocusTo,
  children,
}) => {
  const contentRef = React.useRef<HTMLDivElement>(null);
  const returnFocusToRef = React.useRef(returnFocusTo);
  returnFocusToRef.current = returnFocusTo;
  const [isOpen, setIsOpen] = React.useState(false);

  React.useEffect(() => {
    const modal = findModal(contentRef.current);
    if (!enabled || !modal) return undefined;

    let showOthers: (() => void) | null = null;

    const close = () => {
      if (showOthers) showOthers();
      showOthers = null;
    };

    $(modal)
      .on('shown.modalFocusLock', (e) => {
        if (e.target !== modal) return;
        showOthers = hideOthers(modal);
        setIsOpen(true);
      })
      .on('hidden.modalFocusLock', (e) => {
        if (e.target !== modal) return;
        setIsOpen(false);
        close();
      });

    return () => {
      $(modal).off('.modalFocusLock');
      close();
    };
  }, [enabled]);

  const returnFocus = React.useCallback(() => {
    const target = returnFocusToRef.current && returnFocusToRef.current();
    Promise.resolve().then(() => {
      if (target && document.body.contains(target)) target.focus();
    });
  }, []);

  if (!enabled) return <>{children}</>;

  return (
    <FocusLock
      disabled={!enabled || !isOpen}
      returnFocus={false}
      onDeactivation={returnFocus}
    >
      <div ref={contentRef}>{children}</div>
    </FocusLock>
  );
};

export default ModalFocusLock;
